import { Diff, Omit, IAnyObj } from "./generics";
import ObjectId from "bson-objectid";

export function removeKeys<T, K extends keyof T>(
	obj: T,
	...keys: K[]
): Omit<T, K> {
	const obj2 = { ...obj };
	for (let i = 0; i < keys.length; ++i) {
		delete obj2[keys[i]];
	}
	return obj2;
}

export function getUniqueId(currentIds: number[], numOfIds = 1): number[] {
	const ids = [...currentIds];
	const newIds: number[] = [];
	for (let i = 0; i < numOfIds; ++i) {
		let uid = 0;
		do {
			uid = Math.floor(Math.random() * 1e9);
		} while (ids.indexOf(uid) !== -1);
		newIds.push(uid);
		ids.push(uid);
	}
	return newIds;
}

export function objectValues<T extends {}>(
	obj: T
): Diff<T[keyof T], undefined>[] {
	const keys = Object.keys(obj);
	const arr: Diff<T[keyof T], undefined>[] = [];
	for (let i = 0; i < keys.length; ++i) {
		const val = obj[keys[i]];
		if (val === undefined) continue;
		arr.push(val);
	}
	return arr;
}

export function getUniqueValues<T>(...args: (T[] | Set<T>)[]): T[] {
	const values: T[] = [];
	for (const arg of args) {
		values.push(...arg);
	}
	return [...new Set(values)];
}

export function arrayToObject<T extends {}, K extends keyof T>(
	arr: readonly T[],
	mainKey: K,
	allowMultiple?: false
): { [key: string]: T | undefined };
export function arrayToObject<T extends {}, K extends keyof T>(
	arr: readonly T[],
	mainKey: K,
	allowMultiple: true
): { [key: string]: T[] | undefined };

export function arrayToObject<T extends {}, K extends keyof T>(
	arr: readonly T[],
	mainKey: K,
	allowMultiple: true
): { [key: string]: T[] | undefined };

export function arrayToObject<T extends {}, V>(
	arr: readonly T[],
	fn: (
		item: T,
		index: number,
		orgininalArr: T[]
	) => { key: string | number; value: V } | null,
	allowMultiple?: false
): { [key: string]: V | undefined };
export function arrayToObject<T extends {}, V>(
	arr: readonly T[],
	fn: (item: T) => { key: string | number; value: V } | null,
	allowMultiple: true
): { [key: string]: V[] | undefined };

export function arrayToObject(
	arr: readonly any[],
	mainKey: any,
	allowMultiple = false
): { [key: string]: any } {
	const obj: { [key: string]: any } = {};
	if (!allowMultiple) {
		for (let i = 0; i < arr.length; ++i) {
			let key = arr[i][mainKey as string];
			let value = arr[i];
			if (typeof mainKey === "function") {
				const temp = mainKey(arr[i], i, arr);
				if (!temp) continue;
				key = temp.key;
				value = temp.value;
			}
			obj[key] = value;
		}
	} else {
		for (let i = 0; i < arr.length; ++i) {
			let key = arr[i][mainKey as string];
			let value = arr[i];
			if (typeof mainKey === "function") {
				const temp = mainKey(arr[i], i, arr);
				if (!temp) continue;
				key = temp.key;
				value = temp.value;
			}
			if (!obj[key]) {
				obj[key] = [];
			}
			(obj[key] as any[]).push(value);
		}
	}
	return obj;
}

// tslint:disable-next-line:cognitive-complexity
export function mergeRecursive<T1 extends IAnyObj, T2 extends IAnyObj>(
	object1: T1,
	object2: T2
): T1 & T2 {
	const obj1 = { ...object1 };
	const obj2 = { ...object2 };
	for (const p in obj2) {
		if (obj2.hasOwnProperty(p)) {
			try {
				// Property in destination object set; update its value.
				if (obj2[p].constructor === Object) {
					obj1[p] = mergeRecursive(obj1[p], obj2[p]);
				} else {
					obj1[p] = obj2[p] as any;
					if (obj1[p] === undefined) delete obj1[p];
				}
			} catch (e) {
				// Property in destination object not set; create it and set its value.
				obj1[p] = obj2[p] as any;
				if (obj1[p] === undefined) delete obj1[p];
			}
		}
	}

	return obj1 as any;
}

export function timeDiff(date1: Date | undefined, date2?: Date): number {
	if (!date1) return Infinity;
	try {
		const time2 = date2 ? date2.getTime() : Date.now();
		return time2 - date1.getTime();
	} catch (e) {
		console.log(e);
		return Infinity;
	}
}

export const reflectPromise = <T>(p): Promise<IReflectPromiseRes<T>> =>
	p
		.then(v => ({ v, status: "resolved" }))
		.catch(e => ({ e, status: "rejected" }));
export type IReflectPromiseRes<T> =
	| { v: T; status: "resolved" }
	| { e: Error; status: "rejected" };

export const delayPromise = (milliseconds: number) =>
	new Promise(resolve => setTimeout(resolve, milliseconds));

export function roundNumberDecimalPlaces(value: number, decimals: number) {
	return Number(Math.round(+(+(value + "e" + decimals) + "e-" + decimals)));
}

const reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;
export function jsonDateParser(key: any, value: any) {
	if (typeof value === "string") {
		const a = reISO.exec(value);
		if (a) return new Date(value);
	}
	return value;
}

export function getOldIfUnchanged<T>(newObj: T, oldObj: T): T {
	if (
		newObj === oldObj ||
		JSON.stringify(newObj) === JSON.stringify(oldObj)
	) {
		return oldObj;
	} else {
		return newObj;
	}
}

export function isValidDateString(str: string): boolean {
	return !Number.isNaN(Date.parse(str));
}

export function deepEqual(x: any, y: any) {
	if (x === y) {
		return true;
	}

	if (
		typeof x === "object" &&
		x !== null &&
		typeof y === "object" &&
		y !== null
	) {
		if (x instanceof Date && y instanceof Date) {
			return x.getTime() === y.getTime();
		}

		if (Object.keys(x).length !== Object.keys(y).length) {
			return false;
		}

		for (const prop in x) {
			if (x.hasOwnProperty(prop)) {
				if (y.hasOwnProperty(prop)) {
					if (!deepEqual(x[prop], y[prop])) {
						return false;
					}
				} else {
					return false;
				}
			}
		}

		return true;
	}

	return false;
}

export const generateFakeObjectId = () => new ObjectId().toHexString();

export function subtractSet<T extends string | number>(
	this: Set<T>,
	set2: Iterable<T>
) {
	for (const x of set2) {
		this.delete(x);
	}
	return this;
}

export const isNonNullable = <T extends any>(val: T): val is NonNullable<T> => {
	return val !== undefined && val !== null;
};

export function as<T>(x: any): asserts x is T {
	//
}
